/**
 * Hero区域组件
 * 
 * 大标题 + 副标题 + CTA按钮 + 渐变光晕背景
 * 参考 platform.iflow.cn 设计风格
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { homePageContent } from './homePageContent';

interface HeroSectionProps {
  onStartClick: () => void;
  onLearnMoreClick: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  onStartClick,
  onLearnMoreClick
}) => {
  const { hero } = homePageContent;
  const navigate = useNavigate();

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center bg-white">
      {/* 顶部导航栏 */}
      <nav className="absolute top-0 left-0 right-0 z-20 px-6 md:px-12 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-end">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-ink-600 hover:text-ink-900 transition-colors px-3 py-2"
          >
            登录
          </button>
        </div>
      </nav>

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
        {/* 主标题 */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight">
          <span className="text-ink-900">{hero.title.prefix}</span>
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-paper-700 to-paper-500">
            {hero.title.highlight}
          </span>
          <span className="text-ink-900">{hero.title.suffix}</span>
        </h1>

        {/* 副标题 */}
        <p className="text-xl md:text-2xl text-ink-600 max-w-3xl mx-auto mb-8 leading-relaxed">
          {hero.subtitle}
        </p>

        {/* 描述文字 */}
        <p className="text-base text-ink-500 max-w-2xl mx-auto mb-12">
          {hero.description}
        </p>

        {/* CTA按钮组 */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
          <button
            onClick={onStartClick}
            className="group relative bg-ink-800 text-white px-8 py-4 rounded-xl font-medium text-lg shadow-sm hover:shadow-md hover:bg-ink-700 active:scale-[0.98] transition-all duration-150 w-full sm:w-auto overflow-hidden"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              {hero.cta.primary}
              <svg className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </button>
          <button
            onClick={onLearnMoreClick}
            className="group bg-white text-ink-700 px-8 py-4 rounded-xl font-medium text-lg border border-paper-200 shadow-sm hover:shadow-md hover:border-paper-300 transition-all duration-150 w-full sm:w-auto"
          >
            <span className="flex items-center justify-center gap-2">
              {hero.cta.secondary}
            </span>
          </button>
        </div>

        {/* 特性标签 */}
        <div className="flex flex-wrap justify-center gap-3">
          {hero.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-paper-100 text-sm text-ink-600 shadow-sm hover:shadow-md hover:border-paper-200 transition-all duration-150"
            >
              <svg className="w-4 h-4 text-paper-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* 底部渐变遮罩 */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
};
